import {
  ReactNode,
  useCallback,
  UIEvent,
  useRef,
  useLayoutEffect,
  ComponentProps,
} from "react";
import { LayoutPositioner, generateLayoutElementComponent } from "./Layout";
import {
  ScrollPositionProvider,
  useScrollPosition,
  useScrollListener,
} from "src/modules/useScrollPosition";
import { Box, Image } from "src/UILibrary";
import PullToRefresh from "react-simple-pull-to-refresh";

type PageProps = Omit<ComponentProps<typeof Box>, "css"> & {
  onPullToRefresh?: () => void;
  isRefreshing?: boolean;
  scrollElementRef?: (dom: HTMLElement) => void;
  "data-testid"?: string;
  className?: string;
  children: any;
  pulltoRefreshContentSx?: ComponentProps<typeof Box>["sx"];
};
function PageInternal({
  onPullToRefresh,
  children,
  scrollElementRef,
  sx,
  pulltoRefreshContentSx,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  isRefreshing,
  ...rest
}: PageProps) {
  const contentEl = useRef<HTMLDivElement>(null);
  const onScroll = useScrollListener();

  useLayoutEffect(() => {
    if (scrollElementRef && contentEl?.current) {
      scrollElementRef(contentEl.current);
    }
  }, [onScroll, scrollElementRef, contentEl]);

  const handleScroll = useCallback(
    (e: UIEvent<HTMLElement>) => {
      onScroll(e.currentTarget.scrollTop);
    },
    [onScroll]
  );

  const handleRefresh = async () => {
    await onPullToRefresh?.();
    return true;
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
      {...rest}
    >
      <LayoutPositioner>
        {({ header, footer }) => (
          <>
            {header !== undefined && header !== null && (
              <Header>{header}</Header>
            )}
            <PullToRefresh
              onRefresh={handleRefresh}
              pullingContent={<></>}
              refreshingContent={
                <Image
                  src="/loading-mojo.gif"
                  height={33}
                  width={33}
                  margin="auto"
                  alt=""
                />
              }
            >
              <Box
                sx={{
                  height: "100%",
                  overflow: "auto",
                  ...pulltoRefreshContentSx,
                }}
                data-testid="pull-to-refresh-content"
                ref={contentEl}
                onScroll={handleScroll}
              >
                {children}
              </Box>
            </PullToRefresh>
            {footer !== undefined && footer !== null && (
              <Box flex="none">{footer}</Box>
            )}
          </>
        )}
      </LayoutPositioner>
    </Box>
  );
}

export const Page = (props: PageProps) => (
  <ScrollPositionProvider>
    <PageInternal {...props} />
  </ScrollPositionProvider>
);

// NOTE: Use a separate Header component to prevent Page component from very frequent re-rendering due to vertical scroll
function Header({ children }: { children: ReactNode }) {
  const scrollY = useScrollPosition();
  return (
    <Box
      flex="none"
      boxShadow={
        scrollY > 0 ? "0px 2px 0px rgba(45, 64, 150, 0.06)" : undefined
      }
    >
      {children}
    </Box>
  );
}

Page.Header = generateLayoutElementComponent("header");
Page.Footer = generateLayoutElementComponent("footer");
